// src/cockpit4/components/AuditLogPanel.tsx
// ============================================
// AuditLogPanel - 企业审计日志面板
//
// 白皮书铁律一 + 用户偏好：
//   - real AUDIT LOGS（非模拟/硬编码）
//   - 记录所有登录/登出/注册操作
//   - 持久化到 Obsidian Vault XEAOS/Audit/
//
// 数据来源：queryRecentAuditLogs() 读取 Vault XEAOS/Audit/LoginLog-{date}.md
// 无数据时显示"暂无审计日志"，不展示假数据
// ============================================

import { useEffect, useState, useCallback } from "react";
import { motion } from "framer-motion";
import {
  Shield,
  LogIn,
  LogOut,
  UserPlus,
  KeyRound,
  XCircle,
  CheckCircle2,
  RefreshCw,
  Loader2,
  Calendar,
} from "lucide-react";
import { queryRecentAuditLogs, type AuditEntry } from "../../stores/userStore";

// 操作类型配置
const ACTION_CONFIG: Record<string, { label: string; color: string; icon: typeof LogIn }> = {
  login: { label: "登录", color: "#34D399", icon: LogIn },
  logout: { label: "登出", color: "#9CA3AF", icon: LogOut },
  register: { label: "注册", color: "#60A5FA", icon: UserPlus },
  change_password: { label: "改密", color: "#FBBF24", icon: KeyRound },
  login_failed: { label: "登录失败", color: "#F87171", icon: XCircle },
};

interface AuditLogPanelProps {
  /** 展示最近几天的日志，默认 7 天 */
  days?: number;
  /** 是否 compact 紧凑模式 */
  compact?: boolean;
}

export default function AuditLogPanel({ days = 7, compact = false }: AuditLogPanelProps) {
  const [logs, setLogs] = useState<Array<{ date: string; entries: AuditEntry[] }>>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedDates, setExpandedDates] = useState<Set<string>>(new Set());

  const loadLogs = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await queryRecentAuditLogs(days);
      if (result.success) {
        setLogs(result.logs);
        // 默认展开今天
        const today = new Date().toISOString().slice(0, 10);
        setExpandedDates(new Set([today]));
      } else {
        setLogs([]);
      }
    } catch (err: any) {
      setError(err?.message || "加载审计日志失败");
      setLogs([]);
    } finally {
      setLoading(false);
    }
  }, [days]);

  useEffect(() => {
    loadLogs();
  }, [loadLogs]);

  // 统计
  const totalEntries = logs.reduce((sum, l) => sum + l.entries.length, 0);
  const successCount = logs.reduce(
    (sum, l) => sum + l.entries.filter((e) => e.success).length,
    0
  );
  const failedCount = totalEntries - successCount;

  const toggleDate = (date: string) => {
    setExpandedDates((prev) => {
      const next = new Set(prev);
      if (next.has(date)) next.delete(date);
      else next.add(date);
      return next;
    });
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      className={`rounded-xl border border-white/[0.06] bg-white/[0.02] backdrop-blur-xl overflow-hidden ${compact ? "" : "p-4"}`}
    >
      {/* 头部 */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-white/[0.06]">
        <div className="flex items-center gap-2">
          <Shield size={16} className="text-emerald-400" />
          <span className="text-[14px] font-medium text-white">企业审计日志</span>
          <span className="text-[10px] font-mono text-gray-600">来自 Vault XEAOS/Audit/</span>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-3 text-[11px] font-mono">
            <span className="text-emerald-400">✅ {successCount}</span>
            <span className="text-red-400">❌ {failedCount}</span>
            <span className="text-gray-500">共 {totalEntries} 条</span>
          </div>
          <button
            onClick={loadLogs}
            disabled={loading}
            title="刷新审计日志"
            className="flex items-center gap-1 px-2 py-1 rounded-md border border-emerald-500/30 bg-emerald-500/10 hover:bg-emerald-500/20 transition-colors disabled:opacity-50"
          >
            {loading ? (
              <Loader2 size={11} className="animate-spin text-emerald-400" />
            ) : (
              <RefreshCw size={11} className="text-emerald-400" />
            )}
            <span className="text-[11px] font-mono text-emerald-300">刷新</span>
          </button>
        </div>
      </div>

      {/* 内容区 */}
      <div className={`p-3 ${compact ? "max-h-[400px]" : "max-h-[600px]"} overflow-y-auto`}>
        {loading ? (
          <div className="flex items-center justify-center py-8 text-[12px] text-gray-500">
            <Loader2 size={14} className="animate-spin mr-2" /> 加载审计日志...
          </div>
        ) : error ? (
          <div className="text-[12px] text-red-400 py-4 text-center">
            加载失败：{error}
          </div>
        ) : logs.length === 0 ? (
          <div className="text-[12px] text-gray-600 italic py-8 text-center">
            暂无审计日志
            <div className="text-[10px] text-gray-700 mt-2">
              登录/登出操作会自动记录到 Obsidian Vault XEAOS/Audit/
            </div>
          </div>
        ) : (
          <div className="space-y-2">
            {logs.map(({ date, entries }) => {
              const expanded = expandedDates.has(date);
              const visibleEntries = expanded ? entries : entries.slice(0, 3);
              return (
                <div
                  key={date}
                  className="rounded-lg border border-white/[0.04] bg-white/[0.01] overflow-hidden"
                >
                  {/* 日期头 */}
                  <button
                    onClick={() => toggleDate(date)}
                    className="w-full flex items-center justify-between px-3 py-2 hover:bg-white/[0.02] transition-colors"
                  >
                    <div className="flex items-center gap-2">
                      <Calendar size={12} className="text-sky-400" />
                      <span className="text-[12px] font-mono text-gray-300">{date}</span>
                      <span className="text-[10px] text-gray-600">({entries.length} 条)</span>
                    </div>
                    <span className="text-[10px] text-gray-600">
                      {expanded ? "收起" : "展开全部"}
                    </span>
                  </button>
                  {/* 日志条目 */}
                  <div className="divide-y divide-white/[0.03]">
                    {visibleEntries.map((entry, idx) => {
                      const cfg = ACTION_CONFIG[entry.action] || ACTION_CONFIG.login;
                      const Icon = cfg.icon;
                      return (
                        <div
                          key={idx}
                          className="flex items-center gap-3 px-3 py-2 text-[11px] font-mono"
                        >
                          <Icon size={11} style={{ color: cfg.color }} />
                          <span className="text-gray-400 w-16">
                            {entry.timestamp.slice(11, 19)}
                          </span>
                          <span className="text-gray-200 w-24 truncate">{entry.username}</span>
                          <span style={{ color: cfg.color }} className="w-16">
                            {cfg.label}
                          </span>
                          {entry.success ? (
                            <CheckCircle2 size={10} className="text-emerald-400" />
                          ) : (
                            <XCircle size={10} className="text-red-400" />
                          )}
                          <span className={entry.success ? "text-emerald-400" : "text-red-400"}>
                            {entry.success ? "成功" : "失败"}
                          </span>
                          {entry.role && (
                            <span className="text-gray-600 text-[10px]">[{entry.role}]</span>
                          )}
                          {entry.error && (
                            <span className="text-red-400/70 text-[10px] truncate">
                              · {entry.error}
                            </span>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </motion.div>
  );
}
